import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { Checkbox } from '../../components/ui/Checkbox';
import { logger, generateRequestId } from '../../utils/logger';
import { trackSignUp } from '../../hooks/useGoogleAnalytics';

const SignUp = () => {
  const navigate = useNavigate();
  const { signUp, resendVerificationEmail } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Capture referral code from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams?.get('ref');
    if (refCode) {
      setReferralCode(refCode);
      logger?.info('Referral code captured from URL', { referralCode: refCode });
    }
  }, []);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/?.test(formData?.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    // Confirm password validation
    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // First name validation
    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }

    // Last name validation
    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Terms agreement validation
    if (!agreedToTerms) {
      newErrors.terms = 'You must agree to the Terms and Privacy Policy to continue';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    
    // Input sanitization - remove any control characters
    const sanitizedValue = value?.replace(/[\r\n\0]/g, '');
    
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
    
    // Clear error for this field when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    if (generalError) {
      setGeneralError('');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setGeneralError('');
    setSuccessMessage('');
    setResendSuccess(false);
    
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      // Store referral code in metadata
      const metadata = {
        first_name: formData?.firstName?.trim(),
        last_name: formData?.lastName?.trim(),
        user_type: 'patient'
      };

      if (referralCode) {
        metadata.referral_code = referralCode;
      }

      const result = await signUp(formData?.email, formData?.password, metadata);

      if (result?.error) {
        // ENHANCED ERROR LOGGING: Log full error details to console
        console.error('🔴 SIGNUP RESULT ERROR:', {
          message: result?.error?.message,
          code: result?.error?.code,
          status: result?.error?.status,
          details: result?.error?.details,
          hint: result?.error?.hint,
          fullError: result?.error
        });
        
        // Handle specific error cases
        if (result?.error?.message?.includes('already registered')) {
          setGeneralError('This email is already registered. Please sign in instead.');
        } else if (result?.error?.message?.includes('rate limit')) {
          setGeneralError('Too many attempts. Please wait a few minutes and try again.');
        } else if (result?.error?.message?.includes('network')) {
          setGeneralError('Network error. Please check your connection and try again.');
        } else if (result?.error?.message?.toLowerCase()?.includes('weak') || result?.error?.message?.toLowerCase()?.includes('easy to guess')) {
          // Handle leaked password error specifically
          setGeneralError('This password has been found in a database of leaked passwords and cannot be used for security reasons. Please choose a different password that hasn\'t been compromised in data breaches.');
        } else {
          // Display the actual error message from database
          const errorMsg = result?.error?.message || 'Registration failed. Please try again.';
          const errorDetails = result?.error?.details ? ` (${result?.error?.details})` : '';
          const errorHint = result?.error?.hint ? ` Hint: ${result?.error?.hint}` : '';
          setGeneralError(`${errorMsg}${errorDetails}${errorHint}`);
        }
        logger?.error('Sign up failed', { email: formData?.email, error: result?.error });
      } else {
        // Success - redirect to email verification confirmation page
        logger?.info('Sign up successful', { email: formData?.email });
        trackSignUp('email');
        // Track registration completion
        import('../../hooks/useGoogleAnalytics')?.then(({ trackRegistrationComplete }) => {
          trackRegistrationComplete('email');
        });
        navigate('/email-verification-confirmation', { 
          state: { email: formData?.email },
          replace: true 
        });
      }
    } catch (error) {
      setGeneralError('An unexpected error occurred. Please try again.');
      logger?.error('Sign up error', error, { requestId });
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0 || !registeredEmail) {
      return;
    }

    setResendLoading(true);
    setResendSuccess(false);
    setGeneralError('');

    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { error } = await resendVerificationEmail(registeredEmail);

      if (error) {
        setGeneralError(`Failed to resend verification email: ${error?.message || 'Please try again.'}`);
        logger?.error('Resend verification failed', error, { requestId, email: registeredEmail });
      } else {
        setResendSuccess(true);
        setResendCooldown(60); // 60 second cooldown
        logger?.info('Verification email resent', { requestId, email: registeredEmail });
      }
    } catch (error) {
      setGeneralError('An unexpected error occurred. Please try again.');
      logger?.error('Resend verification error', error, { requestId });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Create Account - MyBodyToday&#8482;</title>
        <meta name="description" content="Create your MyBodyToday account to start tracking your health" />
      </Helmet>
      
      <div className="min-h-screen bg-background px-4 py-8">
        <div className="w-full max-w-md mx-auto">
          {/* Header Section */}
          <div className="text-center mb-8">
            <Link to="/sign-in" className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <img src="/assets/images/MyBodyToday_logo_design2-removebg-preview1-1770673381636.png" alt="MyBodyToday Logo" className="w-12 h-12 object-contain" />
            </Link>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Create Account
            </h1>
            <p className="text-muted-foreground">
              Start your health tracking journey today
            </p>
          </div>

          {/* Medical Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-xs font-semibold text-amber-900 mb-1">
                  Medical Disclaimer
                </h3>
                <p className="text-xs text-amber-800 leading-relaxed">
                  This app is for symptom tracking only and does not provide medical advice, diagnosis, or treatment. Always consult healthcare professionals for medical guidance.
                </p>
              </div>
            </div>
          </div>

          {/* Sign Up Form */}
          <div className="bg-card rounded-lg shadow-therapeutic-lg border border-border p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Success Message */}
              {successMessage && (
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-green-50 border border-green-200">
                    <Icon name="CheckCircle" size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-green-800 font-medium mb-1">{successMessage}</p>
                      <p className="text-xs text-green-700">
                        Check your spam folder if you don't see the email within a few minutes.
                      </p>
                    </div>
                  </div>

                  {/* Resend Verification Email Section */}
                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="flex items-start gap-3 mb-3">
                      <Icon name="Mail" size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-blue-900 mb-1">
                          Didn't receive the email?
                        </h3>
                        <p className="text-xs text-blue-800">
                          Click the button below to receive a new verification link.
                        </p>
                      </div>
                    </div>

                    {resendSuccess && (
                      <div className="flex items-start gap-2 p-3 rounded-md bg-green-100 border border-green-300 mb-3">
                        <Icon name="CheckCircle" size={16} className="text-green-700 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-green-800 font-medium">
                          New verification email sent! Please check your inbox.
                        </p>
                      </div>
                    )}

                    <Button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={resendLoading || resendCooldown > 0}
                      variant="outline"
                      className="w-full"
                    >
                      {resendLoading ? (
                        <>
                          <Icon name="Loader" size={16} className="animate-spin mr-2" />
                          Sending...
                        </>
                      ) : resendCooldown > 0 ? (
                        <>
                          <Icon name="Clock" size={16} className="mr-2" />
                          Resend in {resendCooldown}s
                        </>
                      ) : (
                        <>
                          <Icon name="Mail" size={16} className="mr-2" />
                          Resend Verification Email
                        </>
                      )}
                    </Button>

                    {resendCooldown > 0 && (
                      <p className="text-xs text-blue-700 text-center mt-2">
                        You can request a new link in {resendCooldown} second{resendCooldown !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* General Error Message */}
              {generalError && (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                  <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive">{generalError}</p>
                </div>
              )}

              {/* Name Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  type="text"
                  name="firstName"
                  label="First Name"
                  placeholder="John"
                  value={formData?.firstName}
                  onChange={handleChange}
                  error={errors?.firstName}
                  required
                  disabled={loading}
                  autoComplete="given-name"
                  tabIndex={1}
                  maxLength={50}
                />
                <Input
                  type="text"
                  name="lastName"
                  label="Last Name"
                  placeholder="Doe"
                  value={formData?.lastName}
                  onChange={handleChange}
                  error={errors?.lastName}
                  required
                  disabled={loading}
                  autoComplete="family-name"
                  tabIndex={2}
                  maxLength={50}
                />
              </div>

              {/* Email Input */}
              <Input
                type="email"
                name="email"
                label="Email Address"
                placeholder="john.doe@example.com"
                value={formData?.email}
                onChange={handleChange}
                error={errors?.email}
                required
                disabled={loading}
                autoComplete="email"
                inputMode="email"
                tabIndex={3}
              />

              {/* Password Input */}
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  label="Password"
                  placeholder="••••••••"
                  value={formData?.password}
                  onChange={handleChange}
                  error={errors?.password}
                  required
                  disabled={loading}
                  autoComplete="new-password"
                  tabIndex={4}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                  disabled={loading}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                </button>
              </div>

              {/* Confirm Password Input */}
              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  label="Confirm Password"
                  placeholder="••••••••"
                  value={formData?.confirmPassword}
                  onChange={handleChange}
                  error={errors?.confirmPassword}
                  required
                  disabled={loading}
                  autoComplete="new-password"
                  tabIndex={5}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                  disabled={loading}
                  tabIndex={-1}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
                </button>
              </div>

              {/* Password Requirements */}
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-xs text-muted-foreground mb-2 font-medium">Password must contain:</p>
                <ul className="space-y-1 text-xs text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <Icon name="Check" size={14} className="text-teal-600" />
                    At least 8 characters
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="Check" size={14} className="text-teal-600" />
                    One uppercase letter
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="Check" size={14} className="text-teal-600" />
                    One lowercase letter
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="Check" size={14} className="text-teal-600" />
                    One number
                  </li>
                </ul>
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="flex items-start gap-2">
                    <Icon name="Shield" size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-700 leading-relaxed">
                      <span className="font-semibold">Security Check:</span> Your password is checked against a database of leaked passwords from data breaches. If your password has been compromised before, you'll need to choose a different one.
                    </p>
                  </div>
                </div>
              </div>

              {/* Consent Checkbox */}
              <div className="mb-6">
                <Checkbox
                  id="terms-consent"
                  checked={agreedToTerms}
                  onChange={(e) => {
                    setAgreedToTerms(e?.target?.checked);
                    if (errors?.terms) {
                      setErrors(prev => ({ ...prev, terms: '' }));
                    }
                  }}
                  label={
                    <span className="text-sm text-foreground">
                      I agree to the{' '}
                      <Link 
                        to="/terms-and-conditions" 
                        target="_blank"
                        className="text-primary hover:underline font-medium"
                      >
                        Terms and Conditions
                      </Link>
                      {' '}and{' '}
                      <Link 
                        to="/privacy-policy" 
                        target="_blank"
                        className="text-primary hover:underline font-medium"
                      >
                        Privacy Policy
                      </Link>
                    </span>
                  }
                  error={errors?.terms}
                  required
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full min-h-[44px] text-base"
                tabIndex={6}
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Icon name="Loader2" size={20} className="animate-spin" />
                    Creating Account...
                  </span>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>

            {/* Sign In Link */}
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link 
                  to="/sign-in" 
                  className="text-primary hover:text-primary/80 font-medium"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </div>

          {/* Support Contact Section - Accessible without login */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <Icon name="HelpCircle" size={20} className="text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-blue-900 mb-1">
                  Need Help?
                </h3>
                <p className="text-sm text-blue-800 mb-3">
                  Having trouble with email verification or account creation? Contact our support team.
                </p>
              </div>
            </div>
            <div className="space-y-3 ml-13">
              <div className="flex items-center gap-3">
                <Icon name="Mail" size={18} className="text-blue-600 flex-shrink-0" />
                <a
                  href="mailto:support@mybodytoday1.com"
                  className="text-blue-700 hover:text-blue-900 font-medium underline"
                >
                  support@mybodytoday1.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Icon name="Phone" size={18} className="text-blue-600 flex-shrink-0" />
                <a 
                  href="tel:+1-800-555-0123" 
                  className="text-blue-700 hover:text-blue-900 font-medium"
                >
                  1-800-555-0123
                </a>
              </div>
              <p className="text-xs text-blue-700 mt-2">
                Support hours: Monday-Friday, 9 AM - 6 PM EST
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignUp;